import { NgComponentOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  inject,
  Injector,
  input,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageContext, SlugPage } from '@pages/slug-page/models';
import { GetComponentByTypePipe } from '@pages/slug-page/pipe';
import { hasGravity } from '@pages/slug-page/utils';
import { BreadcrumbComponent } from '@shared/components/breadcrumb';
import { BrowserStateService } from '@shared/services/browser-state';
import { LogsService } from '@shared/services/logs';
import { LogLevel } from '@shared/services/logs/enums';
import { PageStore } from '@stores';
import { distinctUntilChanged, fromEvent, map, tap } from 'rxjs';

@Component({
  imports: [NgComponentOutlet, GetComponentByTypePipe, BreadcrumbComponent],
  templateUrl: './slug-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SlugPageComponent {
  pageData = input<SlugPage>();
  hasBreadcrumb = inject(PageStore).selectPath((state) => state.config.breadcrumb.items?.length > 0);
  protected readonly pageContext = computed<readonly PageContext[] | undefined>(() => {
    const blocks = this.pageData()?.blocks ?? [];
    if (blocks.length === 0) return [];
    const has = (b: unknown) => hasGravity(b, { validateShape: true });
    return this.buildPageContext(blocks, has);
  });
  private readonly logs = inject(LogsService);
  private readonly browserStateService = inject(BrowserStateService);
  private readonly window = inject(DOCUMENT).defaultView as Window;
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  constructor() {
    effect(() => {
      this.logs.trace('SlugPageComponent', 'Page data:', this.pageData());
    });

    afterNextRender(() => {
      fromEvent(this.window, 'scroll')
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(() => this.window.scrollY),
          distinctUntilChanged(),
          tap((scrollYPosition) => {
            this.browserStateService.scrollYPosition.set(this.pageData()?.url ?? '', scrollYPosition);
          })
        )
        .subscribe();

      effect(
        () => {
          const url = this.pageData()?.url ?? '';
          const popstate = untracked(this.browserStateService.popstateEvent);
          if (popstate) {
            const scrollYPosition = this.browserStateService.scrollYPosition.get(url) ?? 0;
            this.logs.groupStart(LogLevel.DEBUG, true, 'SlugPageComponent', 'Browser back navigation detected');
            this.logs.debug('', `Restoring scroll position to ${scrollYPosition}px for "${url}"`);
            this.logs.trace(
              '',
              `Browser navigation state entries:`,
              this.browserStateService.scrollYPosition.entries()
            );
            this.logs.groupEnd(LogLevel.DEBUG);
            this.window.setTimeout(() => {
              this.window.scrollTo({
                top: scrollYPosition,
                behavior: 'smooth',
              });
            }, 400);
          } else {
            this.window.scrollTo({ top: 0, behavior: 'auto' });
          }
        },
        { injector: this.injector }
      );
    });
  }

  private buildPageContext<B>(blocks: readonly B[], hasGravity: (block: B) => boolean): readonly PageContext[] {
    const out = new Array<PageContext>(blocks.length);
    let seenImage = false;

    let i = 0;
    for (const block of blocks) {
      out[i] = {
        position: i + 1,
        isImageInPreviousBlocks: seenImage,
      };
      if (!seenImage && hasGravity(block)) {
        seenImage = true;
      }
      i++;
    }

    return out;
  }
}
